/**
 * A fingerprint of everything that decides how a run turns out.
 *
 * ==========================================================================
 *  WHY
 * ==========================================================================
 *  A replay is only meaningful against the physics it was recorded on. Nudge
 *  `PLAYER.maxSpeed` by 0.1 and every stored replay re-simulates to a different
 *  time - not because anyone cheated, but because it is a different game.
 *
 *  Rather than relying on someone remembering to bump a version number, the
 *  fingerprint is derived from the tuning constants and the level geometry
 *  themselves. Change any of them and the hash changes, old replays stop
 *  validating against the new build, and the leaderboard is forced to be
 *  honest about it.
 *
 *  This is a *fingerprint*, not a security measure: it is public, and a
 *  cheater can trivially send the right one. Its job is to stop the server
 *  from silently validating a replay against physics it was never played on.
 * ==========================================================================
 */

import { AAMUPALA } from '../config/aamupala';
import { PIN, PIN_VIEW } from '../config/pin';
import { SEED_LAYOUT } from '../config/seed';
import { BEER } from '../config/beer';
import {
  DOOR,
  DOUBLE_JUMP,
  GLASS_PAIN,
  HANNU,
  INTERACT,
  KLAPI,
  LOOK,
  MOMENTUM,
  PLAYER,
  SIMULATION_HZ,
  STEERING,
} from '../config/gameplay';
import { INTOXICATION, INTOXICATION_LIMITS, REFERENCE_PROMILLE } from '../config/intoxication';
import { SIMA } from '../config/sima';
import { TEST_LEVEL } from '../config/level';
import { campTentBox, solidColliders, type LevelDefinition } from '../world/Level';
import { LOOK_UNITS_PER_TURN } from './StepInput';

/**
 * FNV-1a, 32 bits, run twice with different offsets to give 64 bits of hex.
 *
 * Deliberately not WebCrypto: this has to produce the same string in the
 * browser and in node, synchronously, with no dependency on either.
 */
function fnv1a(text: string, seed: number): number {
  let hash = seed >>> 0;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i) & 0xff;
    hash = Math.imul(hash, 0x01000193) >>> 0;
    hash ^= text.charCodeAt(i) >>> 8;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

export function hash64(text: string): string {
  const low = fnv1a(text, 0x811c9dc5);
  const high = fnv1a(text, 0x9e3779b9);
  return high.toString(16).padStart(8, '0') + low.toString(16).padStart(8, '0');
}

/**
 * Only the parts of a level the simulation can actually touch. Materials,
 * decor boxes and signs are excluded on purpose - repainting the blockout must
 * not invalidate the leaderboard.
 */
function simulationRelevantLevel(level: LevelDefinition): unknown {
  return {
    name: level.name,
    spawn: level.spawn,
    // The boxes that are actually solid, not the boxes they were authored as -
    // a car with a boot lid contributes two. See `solidColliders`.
    solids: level.solids.flatMap((s) => solidColliders(s)),
    campTents: level.campTents.map((tent) => [tent.name, campTentBox(tent)]),
    sleepers: level.sleepers.map((s) => [
      s.name,
      s.center,
      s.orientation,
      s.length,
      s.width,
      s.height,
      s.y ?? 0,
    ]),
    doors: level.doors.map((d) => [d.id, d.box, d.hinge, d.swing]),
    checkpoints: level.checkpoints.map((c) => [c.id, c.box]),
    finish: level.finish.zone,
    // The tub, and all three numbers of it. The rim's own height and its two
    // radii used to be scenery with a collider in `solids` doing the work;
    // they are physics in their own right now, because Hannu measures his way
    // round the outside of that ring and over the top of it. See `Hannu.ts`.
    hotTub: [
      level.hotTub.center,
      level.hotTub.radius,
      level.hotTub.rimRadius,
      level.hotTub.rimHeight,
      level.hotTub.activation,
      level.hotTub.hannuExitAngle ?? 0,
    ],
    kamiina: [
      level.kamiina.pile,
      level.kamiina.pileHalfWidth,
      level.kamiina.hatch,
      level.kamiina.gate,
      level.kamiina.gatePost,
    ],
    // Breakfast, which is now somewhere the run has to *go*: the square metre
    // of kivetys Kimmo is dealt to on, how close he has to get to it, and the
    // name of the box that is Mäysä. The box itself is in `solids` above, like
    // the veräjä is. Everything else about the man and the pan - his cap, his
    // shirt, which way he points, the food on the plate - is dressing and is
    // deliberately not here; see `AamupalaSpec` and src/config/CLAUDE.md.
    aamupala: level.aamupala
      ? [level.aamupala.station, level.aamupala.reach, level.aamupala.maysa.gate]
      : null,
    // The drink in the juhlasali, which is the same bargain one room on: the
    // glass the run has to fetch, the two nozzles that fill it, and the name of
    // the box that is the porukka. That box itself is in `solids` above, like
    // Mäysä's and the veräjä's. The people standing in it - their faces, their
    // shirts, which way they turn and where they go afterwards - are dressing
    // and are deliberately not here; see `SimaSpec` and src/config/CLAUDE.md.
    sima: level.sima ? [level.sima.glass, level.sima.taps, level.sima.gate, level.sima.additionalGates ?? []] : null,
    // The pane in the pirtti's window, which is now a collider: it stands in
    // the opening until Kimmo punches it out, and putting a fist through it
    // costs `GLASS_PAIN.duration` seconds at `slowFactor`. Both halves of that
    // decide finishing times, so the box is hashed twice over - where the wall
    // is, and what getting past it costs. A boarded pane cannot be punched;
    // `ground` is where the shards land, which is rendering.
    ikkuna: [level.ikkuna.pane, level.ikkuna.outward, level.ikkuna.boarded ?? false],
  };
}

let cached: string | null = null;

/**
 * Stable across processes and builds as long as the tuning does not move.
 * Memoised: the level JSON is a few hundred kilobytes.
 */
export function simulationFingerprint(level: LevelDefinition = TEST_LEVEL): string {
  if (level === TEST_LEVEL && cached !== null) return cached;

  const payload = JSON.stringify({
    // Bumped to 2 when the pirtti's window stopped being a sheet of nothing
    // and became a wall you have to punch out. That is a rule change rather
    // than a number change: every value below stayed exactly where it was, so
    // nothing here would have moved on its own, and a replay recorded when
    // arriving was enough would re-simulate into a man standing in a bedroom
    // looking at unbroken glass. The version is what this field is *for* -
    // see the header, and see `src/world/Ikkuna.ts` for the change itself.
    //
    // And to 3 when the juhlasali got a crowd in it. That one *would* have
    // moved the hash on its own - there is a new collider in `solids` and a new
    // block below - so the bump is belt and braces rather than the only thing
    // carrying it. It is here because it is the same *kind* of change as the
    // pane: a run recorded when walking into the hall was enough re-simulates
    // into a man standing in front of seven people with an empty hand. See
    // `src/world/Sima.ts`.
    //
    // And to 4 when the muurikka got three meats and five vegetables instead of
    // one and two. That one would *not* have moved on its own and the bump is
    // the only thing carrying it: the lists are deliberately outside this
    // payload (see `src/config/aamupala.ts`), and every number in `AAMUPALA`
    // stayed exactly where it was. What changed is the shape of the deal - a
    // round now spends its seed on a slot and two vegetables rather than on a
    // shuffle of three - so the boxes come out in a different order and a
    // replay recorded against the old pan re-simulates into a man eating
    // falafelia on camera. See `src/world/Aamupala.ts`.
    //
    // And to 5 when a door stopped being a thing that happens once. It is the
    // same kind of change as the pane and it is carried by the bump alone:
    // `DOOR` below is untouched - a close takes exactly as long as an open and
    // the collider comes back at the same angle it left - and nothing else
    // here knows how many times F is allowed to reach for one. What changed is
    // that the second press is no longer a no-op, so a replay recorded when it
    // was re-simulates into a man who shut the pirtti's ulko-ovi on himself.
    // See `src/world/Door.ts`.
    //
    // And to 6 when the leaf became the collider. `DOOR` did move this time -
    // `colliderClearAt` is gone and `segments` is in its place - so the hash
    // would have shifted on its own, and so would `level.doors`, because the
    // ulko-ovi is hung on the other face of its wall and hinged on the other
    // jamb. The bump is here anyway because it is the *rule* that changed: a
    // door used to be a doorway that stopped being solid a fifth of the way
    // through the swing, and it is now a metre and a half of timber that
    // sweeps through the room and pushes whoever is standing in it. A replay
    // recorded against the old rule re-simulates into a man walking through a
    // door that is, this time, in the way. See `src/world/Door.ts`.
    // Hannu now waits until the entire player has cleared the cable/car row.
    // His first charge changes even though the tuning and colliders do not.
    // Seeded layouts and replay identity are part of the shared rules.
    // Parking now permutes the original six narrow gaps instead of widening them.
    // Only the right-hand sima tap can fill the glass.
    // Logs can only be picked up in front of the rack, within its width.
    // Alcohol now changes ground grip; timed accents are presentation only.
    // Beer lurches alternate left/right by successful sip count.
    // Sips raise the live dose before movement; initial dose remains run identity.
    // Alcohol delays both jump button edges in shared movement simulation.
    // Hannu cancels airborne jumps and consumes jump input during stagger.
    // Seeded obstacle arrangements and Hannu's climb-out bearing.
    // Seeded exit closures, parking patterns and alternate cable crossing.
    v: 19,
    pin: PIN,
    pinMotion: PIN_VIEW,
    seedLayout: SEED_LAYOUT,
    hz: SIMULATION_HZ,
    lookUnits: LOOK_UNITS_PER_TURN,
    player: PLAYER,
    momentum: MOMENTUM,
    // Every number in it decides how high and how far a runner gets, so all of
    // it is physics. See `src/config/CLAUDE.md`.
    doubleJump: DOUBLE_JUMP,
    beer: BEER,
    alcoholLimits: INTOXICATION_LIMITS.alcoholPromille,
    // Every number in it decides how long the toll at the muurikka takes, so
    // all of it is physics. See `src/config/aamupala.ts`.
    aamupala: AAMUPALA,
    // And both numbers in this one decide how far off the racing line the toll
    // in the juhlasali reaches. See `src/config/sima.ts`.
    sima: SIMA,
    steering: STEERING,
    look: LOOK,
    interact: INTERACT,
    door: DOOR,
    klapi: KLAPI,
    glassPain: GLASS_PAIN,
    hannu: { ...HANNU, shout: undefined, voice: undefined },
    reference: REFERENCE_PROMILLE,
    intoxication: {
      movement: INTOXICATION.movement,
      // The camera block is render-only, so it is deliberately excluded.
    },
    level: simulationRelevantLevel(level),
  });

  const digest = hash64(payload);
  if (level === TEST_LEVEL) cached = digest;
  return digest;
}
